import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { ToastProvider, useToast } from "../../context/ToastContext";


const Toast = ({
  type,
  message,
  description,
  onClose,
  duration = 5000,
  action,
}) => {
  const { toasts, removeToast } = useToast();

  const icons = {
    success: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
      </svg>
    ),
    error: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
      </svg>
    ),
    warning: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
      </svg>
    ),
    info: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
      </svg>
    )
  };

  const getToastStyles = (type) => {
    const baseStyles = "max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden border-l-4";
    
    const typeStyles = {
      success: 'border-l-emerald-500 bg-emerald-50',
      error: 'border-l-red-500 bg-red-50',
      warning: 'border-l-amber-500 bg-amber-50',
      info: 'border-l-blue-500 bg-blue-50'
    };

    return `${baseStyles} ${typeStyles[type] || typeStyles.info}`;
  };

  const getIconStyles = (type) => {
    const styles = {
      success: 'text-emerald-500',
      error: 'text-red-500',
      warning: 'text-amber-500',
      info: 'text-blue-500'
    };
    return styles[type] || styles.info;
  };

  const getTextStyles = (type) => {
    const styles = {
      success: 'text-emerald-800',
      error: 'text-red-800',
      warning: 'text-amber-800',
      info: 'text-blue-800'
    };
    return styles[type] || styles.info;
  };

  const ToastItem = ({ toast }) => (
    <motion.div
      initial={{ opacity: 0, y: -50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -50, scale: 0.9 }}
      transition={{ type: "spring", damping: 25, stiffness: 300 }}
      className={getToastStyles(toast.type)}
    >
      <div className="p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <div className={getIconStyles(toast.type)}>
              {icons[toast.type]}
            </div>
          </div>
          <div className="ml-3 w-0 flex-1 pt-0.5">
            <p className={`text-sm font-medium ${getTextStyles(toast.type)}`}>
              {toast.message}
            </p>
            {toast.description && (
              <p className={`mt-1 text-sm ${getTextStyles(toast.type)} opacity-75`}>
                {toast.description}
              </p>
            )}
            {toast.action && (
              <div className="mt-3 flex space-x-7">
                {toast.action}
              </div>
            )}
          </div>
          <div className="ml-4 flex-shrink-0 flex">
            <button
              className="bg-white rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
              onClick={() => removeToast(toast.id)}
            >
              <span className="sr-only">Close</span>
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );

  useEffect(() => {
    if (!message || !onClose || duration <= 0) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      onClose();
    }, duration);

    return () => clearTimeout(timeoutId);
  }, [message, onClose, duration]);

  if (message) {
    const standaloneToast = {
      id: "standalone-toast",
      type: type || "info",
      message,
      description,
      action,
    };

    return createPortal(
      <div className="fixed inset-0 z-50 flex items-center justify-center px-4 pointer-events-none">
        <AnimatePresence>
          <ToastItem key={standaloneToast.id} toast={standaloneToast} />
        </AnimatePresence>
      </div>,
      document.body
    );
  }

  if (toasts.length === 0) return null;

  return createPortal(
    <div className="fixed top-4 right-4 z-50 space-y-4 max-w-md">
      <AnimatePresence>
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} />
        ))}
      </AnimatePresence>
    </div>,
    document.body
  );
};

export default Toast;
